import { Link } from 'react-router-dom';
import { Mail, ArrowUpRight, HeartPulse, Info } from 'lucide-react';
import { BrandLogo } from './Navbar';
import { HEALTH_CATEGORIES, categoryUrl } from '@/lib/health';

const FooterLinks = ({ title, links }) => (
  <div>
    <h4 className="font-heading text-sm font-semibold text-white mb-5">{title}</h4>
    <ul className="space-y-3">
      {links.map((link) => (
        <li key={link.to}>
          <Link to={link.to} className="group inline-flex items-center gap-1 text-sm text-emerald-100/80 hover:text-white transition-colors">
            {link.label}
            <ArrowUpRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform]" />
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  return (
    <footer className="relative bg-health-emerald-dark text-white font-body overflow-hidden" data-testid="site-footer">
      <svg className="absolute -top-px left-0 w-full h-10 text-background" viewBox="0 0 1440 40" preserveAspectRatio="none" aria-hidden="true">
        <path fill="currentColor" d="M0,0 L1440,0 L1440,10 C1100,45 340,45 0,10 Z" />
      </svg>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Link to="/" className="inline-block mb-6 bg-white rounded-2xl px-4 py-3">
              <BrandLogo />
            </Link>
            <p className="text-emerald-100/80 leading-relaxed max-w-sm mb-6">
              Friendly, over-the-counter health essentials for everyday aches, sniffles, scrapes and home check-ups, delivered to your door.
            </p>
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm">
              <HeartPulse className="h-4 w-4 text-health-peach" />
              Caring for families, one order at a time
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8">
            <FooterLinks
              title="Shop"
              links={[{ to: '/products', label: 'All products' }, ...HEALTH_CATEGORIES.map((c) => ({ to: categoryUrl(c.name), label: c.name })), { to: '/dashboard', label: 'My orders' }]}
            />
            <FooterLinks
              title="Company"
              links={[
                { to: '/about-us', label: 'About us' },
                { to: '/privacy-policy', label: 'Privacy policy' },
                { to: '/terms-conditions', label: 'Terms & conditions' },
              ]}
            />
            <div>
              <h4 className="font-heading text-sm font-semibold text-white mb-5">Contact</h4>
              <a href="mailto:hello@pactac.shop" className="flex items-center gap-3 text-emerald-100/80 hover:text-white transition-colors group" data-testid="contact-email">
                <span className="w-9 h-9 rounded-xl bg-white/10 group-hover:bg-health-coral flex items-center justify-center transition-colors">
                  <Mail className="h-4 w-4" />
                </span>
                <span className="text-sm">hello@pactac.shop</span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 flex items-start gap-3 rounded-2xl bg-white/10 p-4 text-xs text-emerald-50/90 leading-relaxed" data-testid="footer-health-disclaimer">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          All products are over-the-counter. Always read the label and follow the directions for use. If symptoms persist, please talk to a pharmacist, doctor or healthcare professional.
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-emerald-100/70">&copy; 2026 PACTAC Health. All rights reserved.</p>
          <p className="text-xs text-emerald-100/70">Genuine products &middot; Secure checkout &middot; Discreet packaging</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
